import React, { useState, useEffect } from 'react';
import { BarChart3, Plus, Minus, Loader, AlertCircle, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart, Bar } from 'recharts';
import { patientAnalyticsService } from '../../../../services/patientAnalyticsService';

const AnalyticsContent = ({ patientId }) => {
  // State for collapsible vital signs blocks
  const [isTemperatureExpanded, setIsTemperatureExpanded] = useState(false);
  const [isPulseRateExpanded, setIsPulseRateExpanded] = useState(false);
  const [isSpO2Expanded, setIsSpO2Expanded] = useState(false);
  const [isWeightExpanded, setIsWeightExpanded] = useState(false);
  const [isBMIExpanded, setIsBMIExpanded] = useState(false);
  const [isBloodPressureExpanded, setIsBloodPressureExpanded] = useState(false);
  const [isRespiratoryRateExpanded, setIsRespiratoryRateExpanded] = useState(false);
  const [isGlucoseExpanded, setIsGlucoseExpanded] = useState(false);

  // State for analytics data
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  // Load analytics data when component mounts or patientId changes
  useEffect(() => {
    if (patientId) {
      loadAnalyticsData();
    }
  }, [patientId]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`📊 Loading analytics data for patient: ${patientId}`);
      
      const data = await patientAnalyticsService.getPatientAnalytics(patientId);
      const summaryStats = patientAnalyticsService.getAnalyticsSummary(data);
      
      setAnalyticsData(data);
      setSummary(summaryStats);
      
      console.log('✅ Analytics data loaded successfully');
      
    } catch (err) {
      console.error('❌ Error loading analytics data:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Get data for charts (fallback to empty arrays if no data)
  const temperatureData = analyticsData?.temperature || [];
  const pulseRateData = analyticsData?.pulseRate || [];
  const spo2Data = analyticsData?.spo2 || [];
  const weightData = analyticsData?.weight || [];
  const bmiData = analyticsData?.bmi || [];
  const bloodPressureData = analyticsData?.bloodPressure || [];
  const respiratoryData = analyticsData?.respiratoryRate || [];
  const glucoseData = analyticsData?.glucose || [];

  // Loading state
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-64">
        <div className="flex items-center space-x-3">
          <Loader className="h-6 w-6 text-blue-600 animate-spin" />
          <span className="text-gray-600">Loading analytics data...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Analytics</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadAnalyticsData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!analyticsData || !patientAnalyticsService.hasSufficientData(analyticsData)) {
    return (
      <div className="flex-1 flex items-center justify-center h-64">
        <div className="text-center">
          <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Analytics Data Available</h3>
          <p className="text-gray-600">
            This patient doesn't have sufficient appointment history with vital signs to generate analytics.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6">
      {/* Analytics Summary */}
      {analyticsData.summary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Analytics Summary</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total Appointments:</span>
              <span className="font-medium text-gray-900 ml-2">{analyticsData.summary.totalAppointments}</span>
            </div>
            {analyticsData.summary.dateRange.start && (
              <div>
                <span className="text-gray-600">Period:</span>
                <span className="font-medium text-gray-900 ml-2">
                  {patientAnalyticsService.formatDateForPeriod(analyticsData.summary.dateRange.start)} - {patientAnalyticsService.formatDateForPeriod(analyticsData.summary.dateRange.end)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Temperature Block */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <h4 className="font-semibold">Temperature</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsTemperatureExpanded(!isTemperatureExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isTemperatureExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isTemperatureExpanded && (
          <div className="p-6">
            <div className="mb-4">
              <h5 className="text-lg font-semibold text-gray-800 mb-2">Body Temperature Monitor</h5>
              <p className="text-sm text-gray-600 mb-4">Track temperature variations and fever patterns</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={temperatureData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  domain={[36, 38]}
                  tickFormatter={(value) => `${value}°C`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="temp" 
                  stroke="#2563eb" 
                  strokeWidth={3}
                  dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#2563eb', strokeWidth: 2, fill: '#fff' }}
                  name="Temperature (°C)"
                />
                <Line 
                  type="monotone" 
                  dataKey="normal" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Normal Range"
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Current</div>
                <div className="text-lg font-semibold text-blue-600">36.6°C</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Average</div>
                <div className="text-lg font-semibold text-green-600">36.8°C</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Normal</div>
                <div className="text-lg font-semibold text-gray-600">37.0°C</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pulse Rate Block */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <h4 className="font-semibold">Pulse Rate</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsPulseRateExpanded(!isPulseRateExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isPulseRateExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isPulseRateExpanded && (
          <div className="p-6">
            <div className="mb-4">
              <h5 className="text-lg font-semibold text-gray-800 mb-2">Heart Rate Monitor</h5>
              <p className="text-sm text-gray-600 mb-4">Pulse rate tracking and cardiovascular health</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={pulseRateData}>
                <defs>
                  <linearGradient id="pulseGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  domain={[60, 85]}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="rate" 
                  stroke="#ef4444" 
                  fillOpacity={1}
                  fill="url(#pulseGradient)"
                  strokeWidth={3}
                  name="Heart Rate (BPM)"
                />
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Target Rate"
                />
              </AreaChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div className="bg-red-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Current</div>
                <div className="text-lg font-semibold text-red-600">74 BPM</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Resting</div>
                <div className="text-lg font-semibold text-green-600">71 BPM</div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Zone</div>
                <div className="text-lg font-semibold text-blue-600">Optimal</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SpO2 Block */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <h4 className="font-semibold">SpO2</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsSpO2Expanded(!isSpO2Expanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isSpO2Expanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isSpO2Expanded && (
          <div className="p-6">
            <div className="mb-4">
              <h5 className="text-lg font-semibold text-gray-800 mb-2">Oxygen Saturation</h5>
              <p className="text-sm text-gray-600 mb-4">Blood oxygen levels and respiratory efficiency</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={spo2Data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  domain={[90, 100]}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="spo2" 
                  stroke="#06b6d4" 
                  strokeWidth={3}
                  dot={{ fill: '#06b6d4', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#06b6d4', strokeWidth: 2, fill: '#fff' }}
                  name="SpO2 (%)"
                />
                <Line 
                  type="monotone" 
                  dataKey="normal" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Normal Threshold"
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div className="bg-cyan-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Current</div>
                <div className="text-lg font-semibold text-cyan-600">98%</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Average</div>
                <div className="text-lg font-semibold text-green-600">97.6%</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Status</div>
                <div className="text-lg font-semibold text-gray-600">Normal</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Weight Block */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <h4 className="font-semibold">Weight</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsWeightExpanded(!isWeightExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isWeightExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isWeightExpanded && (
          <div className="p-6">
            <div className="mb-4">
              <h5 className="text-lg font-semibold text-gray-800 mb-2">Weight Management</h5>
              <p className="text-sm text-gray-600 mb-4">Track weight changes and progress towards goals</p>
            </div>
            {weightData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={weightData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#666"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#666"
                      fontSize={12}
                      domain={[Math.min(...weightData.map(d => d.weight)) - 2, Math.max(...weightData.map(d => d.weight)) + 2]}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="weight" 
                      fill="#8b5cf6" 
                      name="Weight (kg)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="target" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Target Weight"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Current</div>
                    <div className="text-lg font-semibold text-purple-600">
                      {weightData[weightData.length - 1]?.weight} {weightData[weightData.length - 1]?.unit}
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Target</div>
                    <div className="text-lg font-semibold text-green-600">
                      {weightData[0]?.target} {weightData[0]?.unit}
                    </div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Progress</div>
                    <div className="text-lg font-semibold text-blue-600">
                      {weightData.length > 1 ? 
                        `${(weightData[0]?.weight - weightData[weightData.length - 1]?.weight).toFixed(1)} kg` : 
                        'N/A'
                      }
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No weight data available for this patient
              </div>
            )}
          </div>
        )}
      </div>

      {/* BMI Block */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <h4 className="font-semibold">BMI</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsBMIExpanded(!isBMIExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isBMIExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isBMIExpanded && (
          <div className="p-6">
            <div className="mb-4">
              <h5 className="text-lg font-semibold text-gray-800 mb-2">Body Mass Index</h5>
              <p className="text-sm text-gray-600 mb-4">Monitor BMI trends and health category</p>
            </div>
            {bmiData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={bmiData}>
                    <defs>
                      <linearGradient id="bmiGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#666"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#666"
                      fontSize={12}
                      domain={[Math.min(...bmiData.map(d => d.bmi)) - 1, Math.max(...bmiData.map(d => d.bmi)) + 1]}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="bmi" 
                      stroke="#f59e0b" 
                      fillOpacity={1}
                      fill="url(#bmiGradient)"
                      strokeWidth={3}
                      name="BMI"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="normal" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Normal Range"
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Current</div>
                    <div className="text-lg font-semibold text-amber-600">
                      {bmiData[bmiData.length - 1]?.bmi}
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Category</div>
                    <div className="text-lg font-semibold text-green-600">
                      {bmiData[bmiData.length - 1]?.bmi < 18.5 ? 'Underweight' :
                       bmiData[bmiData.length - 1]?.bmi < 25 ? 'Normal' :
                       bmiData[bmiData.length - 1]?.bmi < 30 ? 'Overweight' : 'Obese'}
                    </div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Trend</div>
                    <div className="text-lg font-semibold text-blue-600">
                      {bmiData.length > 1 ? 
                        (bmiData[bmiData.length - 1]?.bmi < bmiData[0]?.bmi ? '↓ Improving' : '↑ Increasing') : 
                        'Stable'
                      }
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No BMI data available for this patient
              </div>
            )}
          </div>
        )}
      </div>

      {/* Blood Pressure Block */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <h4 className="font-semibold">Blood Pressure</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsBloodPressureExpanded(!isBloodPressureExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isBloodPressureExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isBloodPressureExpanded && (
          <div className="p-6">
            <div className="mb-4">
              <h5 className="text-lg font-semibold text-gray-800 mb-2">Blood Pressure Monitor</h5>
              <p className="text-sm text-gray-600 mb-4">Track systolic and diastolic pressure readings</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={bloodPressureData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  domain={[70, 135]}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="systolic" 
                  stroke="#dc2626" 
                  strokeWidth={3}
                  dot={{ fill: '#dc2626', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#dc2626', strokeWidth: 2, fill: '#fff' }}
                  name="Systolic"
                />
                <Line 
                  type="monotone" 
                  dataKey="diastolic" 
                  stroke="#2563eb" 
                  strokeWidth={3}
                  dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#2563eb', strokeWidth: 2, fill: '#fff' }}
                  name="Diastolic"
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div className="bg-red-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Current</div>
                <div className="text-lg font-semibold text-red-600">121/78</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Average</div>
                <div className="text-lg font-semibold text-green-600">122/79</div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Status</div>
                <div className="text-lg font-semibold text-blue-600">Normal</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Respiratory Rate Block */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <h4 className="font-semibold">Respiratory Rate</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsRespiratoryRateExpanded(!isRespiratoryRateExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isRespiratoryRateExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isRespiratoryRateExpanded && (
          <div className="p-6">
            <div className="mb-4">
              <h5 className="text-lg font-semibold text-gray-800 mb-2">Breathing Pattern</h5>
              <p className="text-sm text-gray-600 mb-4">Monitor respiratory rate and breathing efficiency</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={respiratoryData}>
                <defs>
                  <linearGradient id="respiratoryGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#059669" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  domain={[14, 18]}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="rate" 
                  stroke="#059669" 
                  fillOpacity={1}
                  fill="url(#respiratoryGradient)"
                  strokeWidth={3}
                  name="Breaths per minute"
                />
              </AreaChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div className="bg-emerald-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Current</div>
                <div className="text-lg font-semibold text-emerald-600">15 BPM</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Average</div>
                <div className="text-lg font-semibold text-green-600">15.7 BPM</div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Status</div>
                <div className="text-lg font-semibold text-blue-600">Normal</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Glucose Block */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <h4 className="font-semibold">Blood Glucose</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsGlucoseExpanded(!isGlucoseExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isGlucoseExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isGlucoseExpanded && (
          <div className="p-6">
            <div className="mb-4">
              <h5 className="text-lg font-semibold text-gray-800 mb-2">Blood Sugar Monitor</h5>
              <p className="text-sm text-gray-600 mb-4">Track glucose levels and diabetic control</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={glucoseData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  domain={[80, 130]}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="glucose" 
                  fill="#8b5cf6" 
                  name="Fasting Glucose (mg/dL)"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="postMeal" 
                  fill="#ec4899" 
                  name="Post-meal Glucose (mg/dL)"
                  radius={[4, 4, 0, 0]}
                />
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Target Level"
                />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div className="bg-purple-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Fasting</div>
                <div className="text-lg font-semibold text-purple-600">93 mg/dL</div>
              </div>
              <div className="bg-pink-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Post-meal</div>
                <div className="text-lg font-semibold text-pink-600">119 mg/dL</div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">Control</div>
                <div className="text-lg font-semibold text-green-600">Excellent</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsContent;
